/**
 * B4 — Configs e YAML Tab
 * B4.1: ConfigMaps viewer/editor por namespace
 * B4.2: Secrets viewer (mascarados, reveal on click)
 * B4.3: YAML editor inline + syntax highlight
 * B4.4: Diff before/after + Apply do browser
 * B4.5: Historico de mudancas + templates pre-configurados
 */
import { T } from '../../styles/tokens';
import { useEffect, useState } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../../api/client';
import { SkeletonBar, Button } from '../../components/ui';

// ── Docker Images State ──
const dockerImages = signal<Array<{ id: string; name: string; registry_url: string; tag: string; description: string; category: string }>>([]);

// ── Cluster Settings State ──
const clusterSettings = signal<Record<string, { value: string; description: string; updated_at: string }>>({});
const settingsLoading = signal(false);
const settingsSaving = signal(false);
const settingsError = signal('');
const settingsSuccess = signal('');
const settingsForm = signal<Record<string, string>>({});

async function fetchClusterSettings() {
  settingsLoading.value = true;
  try {
    const [data, imagesRes] = await Promise.all([
      api.get<any>('/admin/cluster/metrics/settings'),
      api.get<any>('/cloud/workspaces/docker-images').catch(() => ({ images: [] })),
    ]);
    clusterSettings.value = data.settings || {};
    dockerImages.value = (imagesRes.images || imagesRes.data?.images || []);
    const form: Record<string, string> = {};
    for (const [k, v] of Object.entries(data.settings || {})) {
      form[k] = (v as any).value;
    }
    settingsForm.value = form;
    settingsError.value = '';
  } catch (e: unknown) {
    settingsError.value = e instanceof Error ? e.message : 'Erro ao carregar configurações';
  }
  settingsLoading.value = false;
}

async function saveClusterSettings() {
  settingsSaving.value = true;
  settingsSuccess.value = '';
  try {
    await api.put('/admin/cluster/metrics/settings', { settings: settingsForm.value });
    settingsSuccess.value = 'Configurações salvas com sucesso!';
    await fetchClusterSettings();
    setTimeout(() => { settingsSuccess.value = ''; }, 3000);
  } catch (e: unknown) {
    settingsError.value = e instanceof Error ? e.message : 'Erro ao salvar';
  }
  settingsSaving.value = false;
}

function updateSetting(key: string, value: string) {
  settingsForm.value = { ...settingsForm.value, [key]: value };
}

function SettingsInput({ label, settingKey, explanation }: { label: string; settingKey: string; explanation: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        value={settingsForm.value[settingKey] || ''}
        onInput={(e) => updateSetting(settingKey, (e.target as HTMLInputElement).value)}
        style={{
          width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border,
          border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
          backdropFilter: 'blur(10px)',
        }}
      />
      <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{explanation}</div>
    </div>
  );
}

function SettingsToggle({ label, settingKey, explanation }: { label: string; settingKey: string; explanation: string }) {
  const isOn = settingsForm.value[settingKey] === 'true';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <label style={{ color: T.border, fontSize: 13, fontWeight: 600 }}>{label}</label>
        <Button variant="ghost"
          onClick={() => updateSetting(settingKey, isOn ? 'false' : 'true')}
          style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
            background: isOn ? T.success : T.bgSecondary, position: 'relative', transition: 'background 0.2s',
          }}
        >
          <div style={{
            width: 18, height: 18, borderRadius: '50%', background: '#fff',
            position: 'absolute', top: 3, left: isOn ? 23 : 3, transition: 'left 0.2s',
          }} />
        </Button>
        <span style={{ fontSize: 11, color: isOn ? T.success : T.textSecondary, fontWeight: 600 }}>
          {isOn ? 'Ativado' : 'Desativado'}
        </span>
      </div>
      <div style={{ fontSize: 11, color: T.textSecondary }}>{explanation}</div>
    </div>
  );
}

function SettingsSelect({ label, settingKey, explanation, options }: { label: string; settingKey: string; explanation: string; options: { value: string; label: string }[] }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <select
        value={settingsForm.value[settingKey] || ''}
        onChange={(e) => updateSetting(settingKey, (e.target as HTMLSelectElement).value)}
        style={{
          width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border,
          border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const,
        }}
      >
        <option value="" style={{ background: T.bgHover }}>-- Selecionar --</option>
        {options.map(o => <option key={o.value} value={o.value} style={{ background: T.bgHover }}>{o.label}</option>)}
      </select>
      <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{explanation}</div>
    </div>
  );
}

// ── LLM Providers Customizados State ──
const llmProviders = signal<Array<any>>([]);
const showAddProviderModal = signal(false);
const editingProvider = signal<any>(null);
const providerForm = signal<any>({
  name: '', baseUrl: '', apiKey: '', api: 'openai-compat',
  authHeader: '', customHeaders: '{}', models: '[]', isDefault: false,
});

async function fetchLLMProviders() {
  try {
    const res = await api.get<any>('/admin/cluster/llm-providers');
    llmProviders.value = res.providers || [];
  } catch (e) {
    console.error('Erro ao carregar providers:', e);
  }
}

function ClusterSettingsSection() {
  useEffect(() => { if (Object.keys(clusterSettings.value).length === 0) fetchClusterSettings(); }, []);
  useEffect(() => { fetchLLMProviders(); }, []);

  const cardStyle = {
    background: T.bgOverlay,
    backdropFilter: 'blur(16px)',
    borderRadius: 14,
    padding: 20,
    border: `1px solid ${T.whiteAlpha8}`,
    boxShadow: `0 8px 32px ${T.blackAlpha30}`,
  };

  if (settingsLoading.value) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: T.border, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        Configurações do Cluster
      </h3>

      <div style={{ padding: 12, background: T.primaryBg, borderRadius: 8, border: `1px solid ${T.primaryBgStrong}`, marginBottom: 16, fontSize: 13, color: T.primary }}>
        Configurações de infraestrutura do cluster. Recursos por plano (CPU, memória, storage) ficam na aba <strong>Planos</strong>.
      </div>

      {settingsError.value && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: T.danger, marginBottom: 16, fontSize: 13 }}>{settingsError.value}</div>
      )}
      {settingsSuccess.value && (
        <div style={{ padding: 12, background: T.successBg, borderRadius: 8, color: T.success, marginBottom: 16, fontSize: 13 }}>{settingsSuccess.value}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Card 2: Auto-Suspend */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: T.warning, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            Auto-Suspend (Hibernação)
          </h4>
          <SettingsToggle label="Auto-Suspend" settingKey="auto_suspend_enabled" explanation="Suspende automaticamente ambientes sem atividade, economizando recursos. O pod é escalado para 0 replicas." />
          <SettingsInput label="Timeout (minutos)" settingKey="auto_suspend_minutes" explanation="Tempo em minutos sem atividade antes de suspender o ambiente" />
        </div>

        {/* Card 3: Auto-Resume */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: T.success, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
            Auto-Resume
          </h4>
          <SettingsToggle label="Auto-Resume" settingKey="auto_resume_enabled" explanation="Quando o usuário acessa um ambiente suspenso, ele é reativado automaticamente. Cold start de ~15-30 segundos." />
          <div style={{ padding: '8px 12px', background: T.successBg, borderRadius: 8, fontSize: 12, color: T.success, marginTop: 8 }}>
            Cold start estimado: ~15-30 segundos
          </div>
        </div>

        {/* Card 5: Default Docker Image */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: '#06b6d4', marginBottom: 14 }}>
            Imagem Docker Padrao
          </h4>
          <div style={{ marginBottom: 14 }}>
            <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Imagem padrao para novos ambientes</label>
            <select
              value={settingsForm.value['default_docker_image'] || ''}
              onChange={(e) => updateSetting('default_docker_image', (e.target as HTMLSelectElement).value)}
              style={{
                width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border,
                border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const,
              }}
            >
              <option value="" style={{ background: T.bgHover }}>-- Selecionar --</option>
              {dockerImages.value.map((img) => (
                <option key={img.id} value={img.id} style={{ background: T.bgHover }}>
                  {img.name}{img.tag ? ` (${img.tag})` : ''}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>
              Esta imagem vem pre-selecionada no modal de criar ambiente na pagina de Agentes. Ao adicionar novas versoes, basta trocar aqui.
            </div>
          </div>
          {settingsForm.value['default_docker_image'] && dockerImages.value.length > 0 && (() => {
            const selected = dockerImages.value.find(i => i.id === settingsForm.value['default_docker_image']);
            if (!selected) return null;
            return (
              <div style={{ padding: 12, background: T.primaryBg, borderRadius: 8, border: '1px solid rgba(6,182,212,0.2)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.border, marginBottom: 4 }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{selected.registry_url}:{selected.tag}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{selected.description}</div>
              </div>
            );
          })()}
        </div>

        {/* Card 7: Infraestrutura K8s */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: T.accent, marginBottom: 14 }}>
            Infraestrutura K8s
          </h4>
          <SettingsInput label="Gateway Port" settingKey="gateway_port" explanation="Porta do gateway OpenClaw nos containers. Default: 18789" />
          <SettingsInput label="NODE_OPTIONS Heap (MB)" settingKey="node_options_heap" explanation="Max old space size em MB para NODE_OPTIONS dos containers OpenClaw. Default: 1024" />
          <SettingsInput label="Cloud Domain" settingKey="cloud_domain" explanation="Dominio base para workspaces cloud. Ex: cloud.yourdomain.com" />
          <SettingsInput label="ClusterIssuer Name" settingKey="cluster_issuer" explanation="Nome do ClusterIssuer cert-manager para certificados TLS. Default: letsencrypt-production" />
          <SettingsInput label="GHCR Token" settingKey="ghcr_token" explanation="Token de autenticação para puxar imagens privadas do GitHub Container Registry (ghcr.io)" />
          <SettingsInput label="GHCR User" settingKey="ghcr_user" explanation="Usuário do GitHub para autenticação no GHCR. Default: owner" />
        </div>

        {/* Card 8: OpenClaw Gateway */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: T.danger, marginBottom: 14 }}>
            OpenClaw Gateway
          </h4>
          <SettingsToggle label="Chat Completions API" settingKey="gateway_chat_completions_enabled"
            explanation="Habilita endpoint HTTP /v1/chat/completions no gateway. Necessario para o Chat Hub se comunicar com workspaces." />
          <SettingsSelect label="Bind Mode" settingKey="gateway_bind_mode"
            explanation="Modo de escuta do gateway. 'lan' = escuta em 0.0.0.0 (necessario para K8s Service). 'loopback' = apenas 127.0.0.1."
            options={[
              { value: 'lan', label: 'LAN (0.0.0.0) — recomendado para K8s' },
              { value: 'loopback', label: 'Loopback (127.0.0.1)' },
              { value: 'auto', label: 'Auto' },
            ]}
          />
          <SettingsInput label="Trusted Proxies" settingKey="gateway_trusted_proxies"
            explanation="Redes confiaveis (CIDR). Separar com virgula. Default: 127.0.0.1/32,10.0.0.0/8,172.16.0.0/12" />
        </div>

        {/* Card 8: Defaults para Novos Ambientes */}
        <div style={cardStyle}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: T.success, marginBottom: 14 }}>
            Defaults para Novos Ambientes
          </h4>
          <SettingsSelect label="Idioma padrao" settingKey="default_language"
            explanation="Idioma padrao do agente em novos ambientes."
            options={[
              { value: 'pt-BR', label: 'Portugues (pt-BR)' },
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Espanol' },
            ]}
          />
          <SettingsInput label="Skills padrao" settingKey="default_skills"
            explanation="Skills que vem pre-instaladas em novos ambientes. Separar com virgula. Ex: web-search,code-runner" />
          <SettingsInput label="Timezone padrao" settingKey="default_timezone"
            explanation="Timezone padrao para novos ambientes. Ex: America/Sao_Paulo" />
          <SettingsToggle label="Telegram habilitado por padrao" settingKey="default_telegram_enabled"
            explanation="Se ativo, novos ambientes ja vem com o plugin Telegram habilitado (ainda precisa do bot token)" />
        </div>
      </div>

      {/* Card: LLM Providers Customizados */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: T.warning, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>LLM Providers Customizados</span>
          <Button variant="ghost"
            onClick={() => {
              providerForm.value = { name: '', baseUrl: '', apiKey: '', api: 'openai-compat', authHeader: '', customHeaders: '{}', models: '[{"id":"auto","name":"Auto"}]', isDefault: false };
              editingProvider.value = null;
              showAddProviderModal.value = true;
            }}
            style={{ padding: '6px 12px', background: T.warning, color: T.text, border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            + Adicionar Provider
          </Button>
        </h4>
        <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 12 }}>
          Adicione providers LLM customizados disponíveis ao criar novos ambientes. O provider marcado como padrão será pré-selecionado.
        </div>
        {llmProviders.value.length === 0 ? (
          <div style={{ padding: 12, background: T.bgActive, borderRadius: 8, color: T.textSecondary, fontSize: 13, textAlign: 'center' }}>
            Nenhum provider customizado. Clique em "+ Adicionar Provider" para começar.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {llmProviders.value.map((prov: any) => (
              <div key={prov.id} style={{
                padding: '10px 12px',
                background: prov.is_default ? T.warningBg : T.bgCardHover,
                border: prov.is_default ? '1px solid rgba(249,115,22,0.4)' : `1px solid ${T.borderHover}`,
                borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.border }}>{prov.name}</span>
                    {prov.is_default ? <span style={{ padding: '2px 6px', background: T.warning, color: T.text, borderRadius: 4, fontSize: 10, fontWeight: 700 }}>PADRÃO</span> : null}
                  </div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>
                    {prov.base_url} • {prov.api} • {(prov.models || []).length} modelo(s)
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="ghost" onClick={() => {
                    providerForm.value = {
                      name: prov.name, baseUrl: prov.base_url, apiKey: '', api: prov.api,
                      authHeader: prov.auth_header || '', customHeaders: JSON.stringify(prov.custom_headers || {}, null, 2),
                      models: JSON.stringify(prov.models || [], null, 2), isDefault: !!prov.is_default,
                    };
                    editingProvider.value = prov;
                    showAddProviderModal.value = true;
                  }} style={{ padding: '4px 10px', background: T.primaryBg, color: T.primary, border: `1px solid ${T.primaryBgStrong}`, borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Editar
                  </Button>
                  <Button variant="ghost" onClick={async () => {
                    if (!confirm(`Deletar provider "${prov.name}"?`)) return;
                    try { await api.delete(`/admin/cluster/llm-providers/${prov.id}`); await fetchLLMProviders(); } catch { alert('Erro ao deletar'); }
                  }} style={{ padding: '4px 10px', background: T.dangerBg, color: '#f87171', border: `1px solid ${T.dangerBgStrong}`, borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    Deletar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Adicionar/Editar Provider */}
      {showAddProviderModal.value && (
        <div style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: T.bgCard, border: `1px solid ${T.borderHover}`, borderRadius: 14, padding: 24, width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' as const }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: T.border, marginBottom: 16 }}>
              {editingProvider.value ? 'Editar Provider' : 'Adicionar Provider'}
            </h3>
            {[
              { label: 'Nome', key: 'name', placeholder: 'Ex: Nexos AI', type: 'text' },
              { label: 'Base URL', key: 'baseUrl', placeholder: 'Ex: https://api.nexos.ai/v1', type: 'text' },
              { label: 'API Key', key: 'apiKey', placeholder: editingProvider.value ? 'Deixe vazio para não alterar' : 'nxa_...', type: 'password' },
              { label: 'Auth Header (opcional)', key: 'authHeader', placeholder: 'Ex: Bearer {apiKey}', type: 'text' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>{f.label}</label>
                <input type={f.type} value={(providerForm.value as any)[f.key]}
                  onInput={(e) => { providerForm.value = { ...providerForm.value, [f.key]: (e.target as HTMLInputElement).value }; }}
                  placeholder={f.placeholder}
                  style={{ width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const }}
                />
              </div>
            ))}
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>API Type</label>
              <select value={providerForm.value.api}
                onChange={(e) => { providerForm.value = { ...providerForm.value, api: (e.target as HTMLSelectElement).value }; }}
                style={{ width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const }}>
                <option value="openai-compat">OpenAI Compatible</option>
                <option value="anthropic">Anthropic</option>
                <option value="google">Google AI</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Modelos (JSON)</label>
              <Textarea value={providerForm.value.models}
                onInput={(e) => { providerForm.value = { ...providerForm.value, models: (e.target as HTMLTextAreaElement).value }; }}
                placeholder='[{"id":"auto","name":"Auto"}]'
                style={{ width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, minHeight: 80, fontFamily: 'monospace', boxSizing: 'border-box' as const }}
              />
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>Array de objetos: [{"{"}"id":"model-id","name":"Nome"{"}"}]</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ color: T.border, fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Custom Headers (JSON, opcional)</label>
              <Textarea value={providerForm.value.customHeaders}
                onInput={(e) => { providerForm.value = { ...providerForm.value, customHeaders: (e.target as HTMLTextAreaElement).value }; }}
                placeholder='{}'
                style={{ width: '100%', padding: '8px 12px', background: T.bgCardHover, color: T.border, border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 13, minHeight: 50, fontFamily: 'monospace', boxSizing: 'border-box' as const }}
              />
            </div>
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" checked={providerForm.value.isDefault}
                onChange={(e) => { providerForm.value = { ...providerForm.value, isDefault: (e.target as HTMLInputElement).checked }; }}
                style={{ width: 16, height: 16 }} />
              <label style={{ color: T.border, fontSize: 13, fontWeight: 600 }}>Marcar como padrão</label>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <Button variant="ghost" onClick={async () => {
                try {
                  let models: any[];
                  try { models = JSON.parse(providerForm.value.models); } catch { alert('JSON de modelos inválido'); return; }
                  let customHeaders: any;
                  try { customHeaders = providerForm.value.customHeaders ? JSON.parse(providerForm.value.customHeaders) : undefined; } catch { customHeaders = undefined; }
                  const payload = {
                    name: providerForm.value.name, baseUrl: providerForm.value.baseUrl,
                    apiKey: providerForm.value.apiKey || undefined, api: providerForm.value.api,
                    authHeader: providerForm.value.authHeader || undefined,
                    customHeaders: customHeaders && Object.keys(customHeaders).length > 0 ? customHeaders : undefined,
                    models, isDefault: providerForm.value.isDefault,
                  };
                  if (editingProvider.value) {
                    await api.put(`/admin/cluster/llm-providers/${editingProvider.value.id}`, payload);
                  } else {
                    await api.post('/admin/cluster/llm-providers', payload);
                  }
                  await fetchLLMProviders();
                  showAddProviderModal.value = false;
                } catch { alert('Erro ao salvar provider'); }
              }} style={{ flex: 1, padding: 10, background: T.warning, color: T.text, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Salvar
              </Button>
              <Button variant="ghost" onClick={() => { showAddProviderModal.value = false; }}
                style={{ flex: 1, padding: 10, background: T.bgCardHover, color: T.textSecondary, border: `1px solid ${T.borderHover}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}

      <Button variant="ghost"
        onClick={saveClusterSettings}
        disabled={settingsSaving.value}
        style={{
          padding: '12px 32px', background: settingsSaving.value ? T.bgSecondary : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          color: T.text, border: 'none', borderRadius: 10, cursor: settingsSaving.value ? 'not-allowed' : 'pointer',
          fontSize: 14, fontWeight: 700, boxShadow: `0 4px 16px ${T.primaryBgStrong}`,
        }}
      >
        {settingsSaving.value ? 'Salvando...' : 'Salvar Configurações'}
      </Button>
    </div>
  );
}

const colors = {
  bg: T.bg, card: T.bgCard, cardBorder: T.bgHover,
  text: T.border, textMuted: T.textSecondary,
  green: T.success, red: T.danger, yellow: T.warning, blue: T.primary, purple: T.purple,
};

const configsData = signal<any>(null);
const configsLoading = signal(false);
const configsError = signal('');
const configSection = signal<'configmaps' | 'secrets' | 'editor' | 'templates'>('configmaps');
const selectedNamespace = signal('default');
const expandedConfig = signal<string | null>(null);
const revealedSecrets = signal<Set<string>>(new Set());
const editorContent = signal('');
const editorOriginal = signal('');
const editorTarget = signal({ kind: '', namespace: '', name: '' });
const showDiff = signal(false);
const configHistory = signal<any[]>([]);

async function fetchConfigs() {
  configsLoading.value = true;
  try {
    const data = await api.get<any>(`/admin/cluster/metrics/configs?namespace=${selectedNamespace.value}`);
    configsData.value = data;
    configsError.value = '';
  } catch (e: unknown) {
    configsError.value = e instanceof Error ? e.message : 'Erro ao carregar configs';
  }
  configsLoading.value = false;
}

async function applyYaml() {
  if (!confirm('Aplicar as alteracoes? Isso modifica o recurso no cluster.')) return;
  try {
    await api.post('/admin/cluster/metrics/configs/apply', {
      ...editorTarget.value,
      content: editorContent.value,
    });
    alert('Aplicado com sucesso!');
    configHistory.value = [
      { timestamp: new Date().toISOString(), kind: editorTarget.value.kind, name: editorTarget.value.name, action: 'update', user: 'admin' },
      ...configHistory.value,
    ];
    await fetchConfigs();
  } catch (e: unknown) {
    alert('Erro ao aplicar: ' + (e instanceof Error ? e.message : 'Erro'));
  }
}

function toggleReveal(key: string) {
  const s = new Set(revealedSecrets.value);
  if (s.has(key)) s.delete(key); else s.add(key);
  revealedSecrets.value = s;
}

function openEditor(kind: string, namespace: string, name: string, data: Record<string, string>) {
  const yaml = Object.entries(data).map(([k, v]) => `${k}: ${v}`).join('\n');
  editorContent.value = yaml;
  editorOriginal.value = yaml;
  editorTarget.value = { kind, namespace, name };
  showDiff.value = false;
  configSection.value = 'editor';
}

// Simple syntax highlighting for YAML (CSS-based via pre)
function highlightYaml(text: string): any[] {
  return text.split('\n').map((line, i) => {
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0 && !line.startsWith('#') && !line.startsWith(' ')) {
      const key = line.substring(0, colonIdx);
      const value = line.substring(colonIdx);
      return (
        <div key={i}>
          <span style={{ color: colors.blue }}>{key}</span>
          <span style={{ color: colors.text }}>{value}</span>
        </div>
      );
    }
    if (line.startsWith('#')) {
      return <div key={i} style={{ color: T.textMuted }}>{line}</div>;
    }
    return <div key={i} style={{ color: colors.text }}>{line}</div>;
  });
}

// Simple diff
function computeDiff(original: string, modified: string) {
  const origLines = original.split('\n');
  const modLines = modified.split('\n');
  const maxLen = Math.max(origLines.length, modLines.length);
  const result: Array<{ type: 'same' | 'added' | 'removed' | 'changed'; orig: string; mod: string }> = [];
  for (let i = 0; i < maxLen; i++) {
    const o = origLines[i] || '';
    const m = modLines[i] || '';
    if (o === m) result.push({ type: 'same', orig: o, mod: m });
    else if (!o) result.push({ type: 'added', orig: '', mod: m });
    else if (!m) result.push({ type: 'removed', orig: o, mod: '' });
    else result.push({ type: 'changed', orig: o, mod: m });
  }
  return result;
}

const TEMPLATES = [
  { name: 'ConfigMap Basico', kind: 'ConfigMap', yaml: `apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: meu-config\n  namespace: default\ndata:\n  APP_ENV: production\n  LOG_LEVEL: info\n  PORT: "3000"` },
  { name: 'Secret Opaque', kind: 'Secret', yaml: `apiVersion: v1\nkind: Secret\nmetadata:\n  name: meu-secret\n  namespace: default\ntype: Opaque\nstringData:\n  API_KEY: sua-chave-aqui\n  DB_PASSWORD: senha-segura` },
  { name: 'ConfigMap NGINX', kind: 'ConfigMap', yaml: `apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: nginx-config\ndata:\n  nginx.conf: |\n    server {\n      listen 80;\n      location / {\n        proxy_pass http://backend:3000;\n      }\n    }` },
  { name: 'Secret TLS', kind: 'Secret', yaml: `apiVersion: v1\nkind: Secret\nmetadata:\n  name: tls-secret\ntype: kubernetes.io/tls\ndata:\n  tls.crt: <base64-cert>\n  tls.key: <base64-key>` },
  { name: 'ConfigMap Env File', kind: 'ConfigMap', yaml: `apiVersion: v1\nkind: ConfigMap\nmetadata:\n  name: env-config\ndata:\n  NODE_ENV: production\n  DATABASE_URL: postgres://user:pass@host:5432/db\n  REDIS_URL: redis://redis:6379\n  CORS_ORIGIN: https://app.example.com` },
];

export function ConfigsTab() {
  useEffect(() => { if (!configsData.value) fetchConfigs(); }, []);

  const data = configsData.value;

  return (
    <div>
      {/* Cluster Settings Section */}
      <ClusterSettingsSection />

      <div style={{ borderTop: `1px solid ${T.whiteAlpha8}`, paddingTop: 20, marginTop: 8 }} />

      {configsError.value && (
        <div style={{ padding: 12, background: T.dangerBg, borderRadius: 8, color: colors.red, marginBottom: 16, fontSize: 13 }}>{configsError.value}</div>
      )}

      {/* Section tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {([
          { key: 'configmaps' as const, label: 'ConfigMaps' },
          { key: 'secrets' as const, label: 'Secrets' },
          { key: 'editor' as const, label: 'Editor YAML' },
          { key: 'templates' as const, label: 'Templates' },
        ]).map(s => (
          <Button variant="ghost" key={s.key} onClick={() => configSection.value = s.key} style={{
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            background: configSection.value === s.key ? colors.blue : colors.card,
            color: T.text, border: `1px solid ${colors.cardBorder}`,
          }}>{s.label}</Button>
        ))}
      </div>

      {/* Namespace selector */}
      {configSection.value !== 'editor' && configSection.value !== 'templates' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <label style={{ color: colors.textMuted, fontSize: 13 }}>Namespace:</label>
          <select value={selectedNamespace.value} onChange={(e) => { selectedNamespace.value = (e.target as HTMLSelectElement).value; fetchConfigs(); }}
            style={{ padding: '6px 12px', background: colors.card, color: colors.text, border: `1px solid ${colors.cardBorder}`, borderRadius: 6, fontSize: 13 }}>
            {(data?.namespaces || ['default']).map((ns: string) => (
              <option key={ns} value={ns}>{ns}</option>
            ))}
          </select>
          <Button variant="primary" size="xs" onClick={fetchConfigs}>Atualizar</Button>
        </div>
      )}

      {configsLoading.value && !data && <div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>}

      {/* B4.1: ConfigMaps */}
      {configSection.value === 'configmaps' && data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: colors.card, borderRadius: 12, padding: '14px 12px', border: `1px solid ${colors.cardBorder}`, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: colors.blue }}>{(data.configMaps || []).length}</div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>ConfigMaps</div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {(data.configMaps || []).map((cm: any) => {
              const isExpanded = expandedConfig.value === `cm-${cm.name}`;
              const dataEntries = Object.entries(cm.data || {});
              return (
                <div key={cm.name} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ color: colors.text, fontWeight: 700, fontSize: 14, fontFamily: 'monospace' }}>{cm.name}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, color: colors.textMuted, background: T.bgCardHover }}>{dataEntries.length} chaves</span>
                      <Button variant="ghost" onClick={() => expandedConfig.value = isExpanded ? null : `cm-${cm.name}`} style={btnS(T.bgSecondary)}>{isExpanded ? 'Fechar' : 'Ver'}</Button>
                      <Button variant="ghost" onClick={() => openEditor('ConfigMap', selectedNamespace.value, cm.name, cm.data || {})} style={btnS(colors.blue)}>Editar</Button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 8, background: T.bg, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}`, fontSize: 12, fontFamily: 'monospace' }}>
                      {dataEntries.length === 0 && <div style={{ color: colors.textMuted }}>Vazio</div>}
                      {dataEntries.map(([k, v]) => (
                        <div key={k} style={{ marginBottom: 4 }}>
                          <span style={{ color: colors.blue }}>{k}</span>
                          <span style={{ color: colors.textMuted }}>: </span>
                          <span style={{ color: colors.text }}>{String(v).length > 200 ? String(v).substring(0, 200) + '...' : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {(data.configMaps || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Nenhum ConfigMap neste namespace</div>
            )}
          </div>
        </div>
      )}

      {/* B4.2: Secrets (masked, reveal on click) */}
      {configSection.value === 'secrets' && data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ background: colors.card, borderRadius: 12, padding: '14px 12px', border: `1px solid ${colors.cardBorder}`, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: colors.purple }}>{(data.secrets || []).length}</div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>Secrets</div>
            </div>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {(data.secrets || []).map((sec: any) => {
              const isExpanded = expandedConfig.value === `sec-${sec.name}`;
              const dataEntries = Object.entries(sec.data || {});
              return (
                <div key={sec.name} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div>
                      <span style={{ color: colors.text, fontWeight: 700, fontSize: 14, fontFamily: 'monospace' }}>{sec.name}</span>
                      <span style={{ color: colors.textMuted, fontSize: 11, marginLeft: 8 }}>Tipo: {sec.type || 'Opaque'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, color: colors.textMuted, background: T.bgCardHover }}>{dataEntries.length} chaves</span>
                      <Button variant="ghost" onClick={() => expandedConfig.value = isExpanded ? null : `sec-${sec.name}`} style={btnS(T.bgSecondary)}>{isExpanded ? 'Fechar' : 'Ver'}</Button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 8, background: T.bg, borderRadius: 8, padding: 12, border: `1px solid ${colors.cardBorder}`, fontSize: 12, fontFamily: 'monospace' }}>
                      {dataEntries.length === 0 && <div style={{ color: colors.textMuted }}>Vazio</div>}
                      {dataEntries.map(([k, v]) => {
                        const revealKey = `${sec.name}-${k}`;
                        const isRevealed = revealedSecrets.value.has(revealKey);
                        return (
                          <div key={k} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: colors.purple }}>{k}</span>
                            <span style={{ color: colors.textMuted }}>: </span>
                            <span style={{ color: colors.text }}>
                              {isRevealed ? String(v) : '***'.repeat(Math.min(6, String(v).length / 3 || 3))}
                            </span>
                            <Button variant="ghost" onClick={() => toggleReveal(revealKey)} style={{
                              padding: '1px 6px', fontSize: 10, background: T.purpleBg,
                              color: colors.purple, border: 'none', borderRadius: 4, cursor: 'pointer',
                            }}>{isRevealed ? 'Ocultar' : 'Revelar'}</Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {(data.secrets || []).length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted }}>Nenhum Secret neste namespace</div>
            )}
          </div>
        </div>
      )}

      {/* B4.3 + B4.4: YAML Editor + Diff */}
      {configSection.value === 'editor' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, color: colors.text, fontWeight: 600, marginBottom: 4 }}>
              {editorTarget.value.kind ? `Editando: ${editorTarget.value.kind}/${editorTarget.value.name} (${editorTarget.value.namespace})` : 'Editor YAML'}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Button variant="ghost" onClick={() => showDiff.value = !showDiff.value} style={btnS(showDiff.value ? colors.purple : T.bgSecondary)}>
                {showDiff.value ? 'Editor' : 'Diff'}
              </Button>
              {editorTarget.value.kind && (
                <Button variant="ghost" onClick={applyYaml} style={btnS(colors.green)}>Aplicar</Button>
              )}
            </div>
          </div>

          {!showDiff.value ? (
            <Textarea
              value={editorContent.value}
              onInput={(e) => editorContent.value = (e.target as HTMLTextAreaElement).value}
              style={{
                width: '100%', minHeight: 400, padding: 16, background: T.bg, color: colors.text,
                border: `1px solid ${colors.cardBorder}`, borderRadius: 8, fontSize: 13,
                fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box',
                lineHeight: 1.6, tabSize: 2,
              }}
              placeholder="Cole seu YAML aqui ou selecione um ConfigMap/Secret para editar..."
              spellcheck={false}
            />
          ) : (
            <div style={{ background: T.bg, borderRadius: 8, padding: 16, border: `1px solid ${colors.cardBorder}`, fontFamily: 'monospace', fontSize: 12, overflow: 'auto', maxHeight: 500 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                <span style={{ color: colors.red, fontSize: 11, fontWeight: 600 }}>- Removido</span>
                <span style={{ color: colors.green, fontSize: 11, fontWeight: 600 }}>+ Adicionado</span>
                <span style={{ color: colors.textMuted, fontSize: 11, fontWeight: 600 }}>  Sem alteracao</span>
              </div>
              {computeDiff(editorOriginal.value, editorContent.value).map((line, i) => (
                <div key={i} style={{
                  padding: '1px 4px', borderRadius: 2,
                  background: line.type === 'added' ? T.successBg : line.type === 'removed' ? T.dangerBg : line.type === 'changed' ? T.warningBg : 'transparent',
                  color: line.type === 'added' ? colors.green : line.type === 'removed' ? colors.red : line.type === 'changed' ? colors.yellow : colors.textMuted,
                }}>
                  {line.type === 'removed' && <span>- {line.orig}</span>}
                  {line.type === 'added' && <span>+ {line.mod}</span>}
                  {line.type === 'changed' && <><div>- {line.orig}</div><div>+ {line.mod}</div></>}
                  {line.type === 'same' && <span>  {line.orig}</span>}
                </div>
              ))}
            </div>
          )}

          {/* B4.5: History */}
          {configHistory.value.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 8 }}>Historico de Mudancas</h4>
              <div style={{ background: colors.card, borderRadius: 8, border: `1px solid ${colors.cardBorder}`, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    {['Data', 'Tipo', 'Nome', 'Acao', 'Usuario'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {configHistory.value.map((h: any, i: number) => (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                        <td style={{ padding: '6px 12px', color: colors.textMuted, fontSize: 11 }}>{new Date(h.timestamp).toLocaleString('pt-BR')}</td>
                        <td style={{ padding: '6px 12px', color: colors.blue, fontSize: 11 }}>{h.kind}</td>
                        <td style={{ padding: '6px 12px', color: colors.text, fontSize: 11, fontFamily: 'monospace' }}>{h.name}</td>
                        <td style={{ padding: '6px 12px', color: h.action === 'delete' ? colors.red : colors.green, fontSize: 11 }}>{h.action}</td>
                        <td style={{ padding: '6px 12px', color: colors.textMuted, fontSize: 11 }}>{h.user}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* B4.5: Templates */}
      {configSection.value === 'templates' && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: colors.text, marginBottom: 16 }}>Templates Pre-configurados</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {TEMPLATES.map((tpl, i) => (
              <div key={i} style={{ background: colors.card, borderRadius: 10, padding: 16, border: `1px solid ${colors.cardBorder}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ color: colors.text, fontWeight: 700, fontSize: 14 }}>{tpl.name}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.primaryBg, color: colors.blue }}>{tpl.kind}</span>
                </div>
                <pre style={{
                  background: T.bg, borderRadius: 8, padding: 12, fontSize: 11, color: colors.textMuted,
                  overflow: 'auto', maxHeight: 150, border: `1px solid ${colors.cardBorder}`, margin: '0 0 8px',
                }}>{tpl.yaml}</pre>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="ghost" onClick={() => {
                    editorContent.value = tpl.yaml;
                    editorOriginal.value = '';
                    editorTarget.value = { kind: tpl.kind, namespace: selectedNamespace.value, name: '' };
                    configSection.value = 'editor';
                  }} style={btnS(colors.blue)}>Usar no Editor</Button>
                  <Button variant="ghost" onClick={() => navigator.clipboard.writeText(tpl.yaml)} style={btnS(T.bgSecondary)}>Copiar</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function btnS(bg: string): Record<string, string | number> {
  return { padding: '4px 10px', background: bg, color: T.text, border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
}
